import React, { useEffect, useState } from 'react';
import GridLayout, { verticalCompactor } from 'react-grid-layout';
import { complianceService } from '../../services/api';
import Navbar from './components/Navbar';
import HeroStats from './components/HeroStats';
import SidebarWidgets from './components/SidebarWidgets';
import LegalUpdates from './components/LegalUpdates';
import RecentComms from './components/RecentComms';
import RepairsWidget from './components/RepairsWidget';
import AwaabLawWidget from './components/AwaabLawWidget';
import Footer from './components/Footer';
import { featureFlags } from '../../utils/featureFlags';
import { calculateComplianceScore } from '../../utils/complianceCalculator';
import { clearSessionPreservingPreferences } from '../../utils/sessionStorage';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { addressToText } from '../../utils/formatAddress';

// Types (to be moved to a shared type file ideally)
export interface DashboardData {
    properties: any[];
    certificates: any[];
    stats: {
        totalProperties: number;
        complianceScore: number;
        expiringSoon: number;
        overdue: number;
    };
    recentActivity: any[];
    exposureScore: any;
    predictiveAlerts: any;
    arrearsCount?: number;
    arrearsAlerts?: any[];
}

const DEFAULT_LAYOUT = [
    { i: 'hero-stats', x: 0, y: 0, w: 12, h: 1, static: true },
    { i: 'legal-exposure', x: 0, y: 1, w: 6, h: 4 },
    { i: 'predictive-alerts', x: 0, y: 5, w: 6, h: 4 },
    // Folded in from the retired Compliance Command Centre tab — placed
    // below the existing left-column widgets so no other widget's
    // coordinates need to move.
    { i: 'repairs-widget', x: 0, y: 9, w: 6, h: 4 },
    { i: 'awaab-law-widget', x: 0, y: 13, w: 6, h: 4 },
    { i: 'sidebar-widgets', x: 6, y: 1, w: 6, h: 5 },
    { i: 'legal-updates', x: 6, y: 6, w: 6, h: 3 },
    { i: 'recent-comms', x: 6, y: 9, w: 6, h: 4 },
];

function loadLayout(userId: string) {
    try {
        const saved = localStorage.getItem(`dashboard-layout-${userId}`);
        if (saved) {
            const parsed = JSON.parse(saved);
            // Migrate existing saved layouts: append any DEFAULT_LAYOUT
            // widgets the user doesn't have yet (e.g. a brand-new widget
            // shipped after they'd already customised their grid) so it
            // doesn't silently stay invisible forever.
            const existingIds = new Set(parsed.map((w: any) => w.i));
            const missing = DEFAULT_LAYOUT.filter(w => !existingIds.has(w.i));
            return missing.length ? [...parsed, ...missing] : parsed;
        }
    } catch { }
    return DEFAULT_LAYOUT;
}

function saveLayout(userId: string, layout: any[]) {
    localStorage.setItem(`dashboard-layout-${userId}`, JSON.stringify(layout));
}

export default function Dashboard({ user }: { user: any }) {
    const [data, setData] = useState<DashboardData | null>(null);
    const [loading, setLoading] = useState(true);
    const [impersonateMode, setImpersonateMode] = useState(false);
    const [impersonateTenant, setImpersonateTenant] = useState<string | null>(null);
    const [layout, setLayout] = useState<any[]>(() => loadLayout(user?.sub || 'default'));

    useEffect(() => {
        // Check for impersonate mode from SuperAdmin
        const impToken = localStorage.getItem('impersonate_token');
        const impTenant = localStorage.getItem('impersonate_tenant');
        if (impToken && impTenant) {
            setImpersonateMode(true);
            setImpersonateTenant(impTenant);
            console.log('[Dashboard] Impersonate mode active for tenant:', impTenant);
        }
    }, []);

    useEffect(() => {
        // Check for success param from FastSpring redirect
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('success') === 'true') {
            // In a real app, use a toast library
            alert("Upgrade Successful! Your account capabilities have been updated.");
            // Ideally refetch user profile/limits here
            window.history.replaceState({}, document.title, window.location.pathname);
        }

        const fetchData = async () => {
            try {
                // Fast path: only wait for the lightweight stats + exceptions endpoints.
                // exposureScore + predictiveAlerts iterate every property and can take
                // 10+ seconds on large portfolios — load them in the background so
                // the dashboard hero stats render immediately.
                const [dashboardRes, exceptionsRes] = await Promise.all([
                    complianceService.getDashboardStats().catch(() => ({ data: { data: {} } })),
                    complianceService.getExceptionFeed().catch(() => ({ data: { data: [] } }))
                ]);

                const dashboardStats = dashboardRes.data?.data || {};
                const exceptions = exceptionsRes?.data?.data || [];
                let exposureData: any = null;
                let predictiveData: any = null;

                // Generate Dynamic Recent Activity from exceptions endpoint
                const alerts: any[] = [];
                exceptions.forEach((e: any) => {
                    if (e.priority === 'critical' || e.priority === 'warning') {
                        alerts.push({
                            type: 'warning',
                            message: e.issue,
                            detail: e.propertyName
                        });
                    }
                });

                if (alerts.length === 0) {
                    alerts.push({
                        type: 'success',
                        message: 'System Status Healthy',
                        detail: 'No immediate actions required'
                    });
                }

                // Arrears summary is now sourced from the dashboard stats summary
                // (or zeros if the backend skipped it under load — see ArrearsService
                // notes in services/dashboard.js).
                const arrearsCount  = dashboardStats.summary?.arrearsCount  || 0;
                const arrearsAlerts = dashboardStats.summary?.arrearsAlerts || [];

                const dashboardData = {
                    properties: [], // Not needed for high-level dashboard display
                    certificates: [], // Not needed for high-level dashboard display
                    stats: {
                        totalProperties: dashboardStats.summary?.totalProperties || 0,
                        complianceScore: dashboardStats.summary?.score || 0,
                        expiringSoon: dashboardStats.certificates?.expiring || 0,
                        overdue: dashboardStats.certificates?.overdue || 0
                    },
                    recentActivity: alerts.slice(0, 5),
                    exposureScore: exposureData,
                    predictiveAlerts: predictiveData,
                    arrearsCount,
                    arrearsAlerts
                };

                console.log('[Dashboard] Final dashboard data:', dashboardData);
                setData(dashboardData);

                // Background: enrich with the slower exposure + predictive endpoints
                // and merge into the dashboard once they arrive. The hero stats are
                // already on screen by this point.
                Promise.all([
                    complianceService.getExposureScore().catch(() => ({ data: { data: null } })),
                    complianceService.getPredictiveAlerts().catch(() => ({ data: { data: null } }))
                ]).then(([exposureRes, alertsRes]: any[]) => {
                    setData(prev => prev ? {
                        ...prev,
                        exposureScore: exposureRes?.data?.data || null,
                        predictiveAlerts: alertsRes?.data?.data || null,
                    } : prev);
                });

            } catch (error: any) {
                console.error("Dashboard data fetch error:", error);
                if (error?.response?.status === 401) {
                    console.log("[Dashboard] Token expired or invalid. Redirecting to login...");
                    clearSessionPreservingPreferences();
                    window.location.href = '/login';
                }
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleLayoutChange = (newLayout: any) => {
        const layoutArr = newLayout as any[];
        setLayout(layoutArr);
        saveLayout(user?.sub || 'default', layoutArr);
    };

    const renderDashboardGrid = () => {
        if (!data) return null;

        // Show ALL widgets — Exposure Score and Predictive Alerts render
        // empty-state copy when there's no data, rather than disappearing
        // entirely (which left the left column blank).
        const visibleItems = ['hero-stats', 'sidebar-widgets', 'legal-exposure', 'predictive-alerts', 'repairs-widget', 'awaab-law-widget', 'legal-updates', 'recent-comms']
            // Repairs + Awaab's Law widgets are a Super Admin per-account
            // toggle (Feature Toggles > Compliance Reporting Widget), not
            // part of the personal drag/resize/hide customisation every
            // other widget here uses — an account it's switched off for
            // never sees them in the grid at all.
            .filter(id => (id !== 'repairs-widget' && id !== 'awaab-law-widget') || featureFlags.COMPLIANCE_REPORTING_WIDGET_ENABLED);
        const filteredLayout = layout.filter(li => visibleItems.includes(li.i));

        return (
            <GridLayout
                className="layout max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
                layout={filteredLayout}
                gridConfig={{ cols: 12, rowHeight: 100 }}
                width={1200}
                onLayoutChange={handleLayoutChange}
                dragConfig={{ handle: '.drag-handle' }}
                resizeConfig={{ enabled: true }}
                compactor={verticalCompactor}
            >
                <div key="hero-stats">
                    <HeroStats stats={data.stats} />
                </div>
                <div key="legal-exposure" className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 overflow-auto">
                    {data.exposureScore?.portfolioScore != null && data.exposureScore?.breakdown ? (<>
                    {/* Original detail body — guarded with fragment so the empty
                        state below renders when the endpoint returns nothing. */}
                        <div className="drag-handle cursor-grab active:cursor-grabbing flex items-center justify-between mb-4">
                            <h2 className="text-lg font-bold text-gray-900"><i className="fas fa-balance-scale text-indigo-500 mr-2"></i>Portfolio Legal Exposure Score</h2>
                            <span className={`px-3 py-1 rounded-full text-sm font-bold ${data.exposureScore.portfolioScore >= 90 ? 'bg-green-100 text-green-700' : data.exposureScore.portfolioScore >= 70 ? 'bg-blue-100 text-blue-700' : data.exposureScore.portfolioScore >= 50 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                                {data.exposureScore.status}
                            </span>
                        </div>
                        <div className="flex items-center mb-6">
                            <div className="text-5xl font-black text-gray-900 mr-4">{data.exposureScore.portfolioScore}%</div>
                            <div className="flex-1">
                                <div className="w-full bg-gray-200 rounded-full h-4">
                                    <div className={`h-4 rounded-full transition-all duration-500 ${data.exposureScore.portfolioScore >= 80 ? 'bg-green-500' : data.exposureScore.portfolioScore >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{ width: `${data.exposureScore.portfolioScore}%` }}></div>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">Across {data.exposureScore.propertyCount} properties</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
                            {[{ label: 'Critical Docs', value: data.exposureScore.breakdown.criticalDocs, max: 50, icon: 'fa-file-shield', color: 'indigo' }, { label: 'Syntax & Wording', value: data.exposureScore.breakdown.syntaxWording, max: 20, icon: 'fa-spell-check', color: 'purple' }, { label: 'Investigatory', value: data.exposureScore.breakdown.investigatory, max: 20, icon: 'fa-search', color: 'blue' }, { label: 'Admin Hygiene', value: data.exposureScore.breakdown.adminHygiene, max: 10, icon: 'fa-broom', color: 'teal' }].map((pillar, i) => (
                                <div key={i} className="bg-gray-50 rounded-xl p-3 text-center">
                                    <i className={`fas ${pillar.icon} text-${pillar.color}-500 text-lg mb-1`}></i>
                                    <div className="text-sm font-semibold text-gray-700">{pillar.label}</div>
                                    <div className="text-xl font-bold text-gray-900">{pillar.value}<span className="text-xs text-gray-400">/{pillar.max}</span></div>
                                </div>
                            ))}
                        </div>
                    </>) : (
                        <>
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-bold text-gray-900"><i className="fas fa-balance-scale text-indigo-500 mr-2"></i>Portfolio Legal Exposure Score</h2>
                                <span className="px-3 py-1 rounded-full text-sm font-bold bg-gray-100 text-gray-500">No data</span>
                            </div>
                            <div className="text-center py-8 text-sm text-gray-500">
                                <i className="fas fa-info-circle text-2xl text-gray-300 mb-2 block"></i>
                                Add a property and upload at least one certificate to start tracking your portfolio's legal exposure.
                            </div>
                        </>
                    )}
                </div>
                <div key="predictive-alerts" className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 overflow-auto">
                    {data.predictiveAlerts && data.predictiveAlerts.alerts?.length > 0 ? (<>
                        <div className="drag-handle cursor-grab active:cursor-grabbing flex items-center justify-between mb-4">
                            <h2 className="text-lg font-bold text-gray-900"><i className="fas fa-crystal-ball text-amber-500 mr-2"></i>Predictive Compliance Alerts</h2>
                            <div className="flex space-x-2">
                                {data.predictiveAlerts.summary.red > 0 && <span className="bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs font-bold">{data.predictiveAlerts.summary.red} Critical</span>}
                                {data.predictiveAlerts.summary.amber > 0 && <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-xs font-bold">{data.predictiveAlerts.summary.amber} High</span>}
                                {data.predictiveAlerts.summary.yellow > 0 && <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full text-xs font-bold">{data.predictiveAlerts.summary.yellow} Early</span>}
                            </div>
                        </div>
                        <div className="space-y-3 max-h-80 overflow-y-auto">
                            {data.predictiveAlerts.alerts.slice(0, 10).map((alert: any) => (
                                <div key={alert.id} className={`flex items-start p-3 rounded-xl border ${alert.severity === 'RED' ? 'bg-red-50 border-red-200' : alert.severity === 'AMBER' ? 'bg-orange-50 border-orange-200' : 'bg-yellow-50 border-yellow-200'}`}>
                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 flex-shrink-0 ${alert.severity === 'RED' ? 'bg-red-200 text-red-700' : alert.severity === 'AMBER' ? 'bg-orange-200 text-orange-700' : 'bg-yellow-200 text-yellow-700'}`}>
                                        <i className={`fas ${alert.daysUntilExpiry === null ? 'fa-exclamation-triangle' : alert.daysUntilExpiry < 0 ? 'fa-times-circle' : 'fa-clock'}`}></i>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-sm text-gray-900">{alert.message}</div>
                                        <div className="text-xs text-gray-500">{addressToText(alert.address)}</div>
                                        <div className="text-xs text-gray-400 mt-1 italic">{alert.action}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>) : (
                        <>
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-bold text-gray-900"><i className="fas fa-crystal-ball text-amber-500 mr-2"></i>Predictive Compliance Alerts</h2>
                                <span className="px-3 py-1 rounded-full text-sm font-bold bg-emerald-100 text-emerald-700">All clear</span>
                            </div>
                            <div className="text-center py-8 text-sm text-gray-500">
                                <i className="fas fa-shield-check text-2xl text-emerald-300 mb-2 block"></i>
                                No upcoming compliance issues. We'll flag certificates here as they approach renewal (60d / 30d / 7d windows).
                            </div>
                        </>
                    )}
                </div>
                {featureFlags.COMPLIANCE_REPORTING_WIDGET_ENABLED && (
                    <div key="repairs-widget" className="overflow-auto">
                        <RepairsWidget />
                    </div>
                )}
                {featureFlags.COMPLIANCE_REPORTING_WIDGET_ENABLED && (
                    <div key="awaab-law-widget" className="overflow-auto">
                        <AwaabLawWidget />
                    </div>
                )}
                <div key="sidebar-widgets" className="overflow-auto">
                    <SidebarWidgets user={user} certificates={data.certificates} activity={data.recentActivity} />
                </div>
                <div key="legal-updates" className="overflow-auto">
                    <LegalUpdates />
                </div>
                <div key="recent-comms" className="overflow-auto">
                    <RecentComms />
                </div>
            </GridLayout>
        );
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-gray-50">
                <div className="animate-pulse flex flex-col items-center">
                    <div className="h-12 w-12 bg-green-200 rounded-full mb-4"></div>
                    <div className="h-4 w-32 bg-gray-200 rounded"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 min-h-screen font-sans">

            <Navbar
                expiringSoon={data?.stats?.expiringSoon || 0}
                overdue={data?.stats?.overdue || 0}
            />

            {/* Impersonate Mode Banner */}
            {impersonateMode && (
                <div className="bg-amber-50 border-b-2 border-amber-300 px-6 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <i className="fas fa-mask text-amber-600 text-lg"></i>
                        <div>
                            <p className="text-sm font-semibold text-amber-900">
                                Impersonate Mode Active
                            </p>
                            <p className="text-xs text-amber-700">
                                You are viewing this account as a super admin. Tenant ID: <code className="bg-amber-100 px-2 py-0.5 rounded font-mono">{impersonateTenant}</code>
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => {
                            localStorage.removeItem('impersonate_token');
                            localStorage.removeItem('impersonate_tenant');
                            setImpersonateMode(false);
                            window.location.href = '/super-admin';
                        }}
                        className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-bold hover:bg-amber-700 transition"
                    >
                        Stop Impersonating
                    </button>
                </div>
            )}

            {renderDashboardGrid()}

            <Footer />
        </div>
    );
}
